import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Room, RoomEvent, Track } from 'livekit-client';
import type { RemoteVideoTrack, RemoteAudioTrack } from 'livekit-client';
import { GreenroomState as GS } from '../../stores/types/types';
import { GetLivekitViewerToken } from '../../connection/livekit';
interface LivekitContextValue {
	tracksMap: Map<string, RemoteVideoTrack>;
	audioTracksMap: Map<string, RemoteAudioTrack>;
}

const LivekitContext = createContext<LivekitContextValue>({ tracksMap: new Map(), audioTracksMap: new Map() });

export function LivekitProvider({ children }: { children: React.ReactNode }) {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting?.meeting);
	const ingressSessions = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);

	const [tracksMap, setTracksMap] = useState<Map<string, RemoteVideoTrack>>(new Map());
	const [audioTracksMap, setAudioTracksMap] = useState<Map<string, RemoteAudioTrack>>(new Map());
	const roomRef = useRef<Room | null>(null);
	const hasSessionsRef = useRef(false);

	useEffect(() => {
		const hasSessions = ingressSessions.length > 0;

		if (!hasSessions) {
			if (roomRef.current) {
				roomRef.current.disconnect();
				roomRef.current = null;
				setTracksMap(new Map());
				setAudioTracksMap(new Map());
			}
			hasSessionsRef.current = false;
			return;
		}

		// Already connected — new sessions added to the same room, no reconnect needed
		if (roomRef.current && hasSessionsRef.current) return;

		if (!token || !meetingId) return;
		hasSessionsRef.current = true;

		GetLivekitViewerToken(token, meetingId).then((result) => {
			if (!result?.token || !result?.serverUrl || roomRef.current) return;

			const room = new Room();
			roomRef.current = room;

			room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
				if (track.kind === Track.Kind.Video) {
					setTracksMap((prev) => new Map(prev).set(participant.identity, track as RemoteVideoTrack));
				} else if (track.kind === Track.Kind.Audio) {
					setAudioTracksMap((prev) => new Map(prev).set(participant.identity, track as RemoteAudioTrack));
				}
			});

			room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
				if (track.kind === Track.Kind.Video) {
					setTracksMap((prev) => {
						const next = new Map(prev);
						next.delete(participant.identity);
						return next;
					});
				} else if (track.kind === Track.Kind.Audio) {
					setAudioTracksMap((prev) => {
						const next = new Map(prev);
						next.delete(participant.identity);
						return next;
					});
				}
			});

			room.on(RoomEvent.Disconnected, () => {
				setTracksMap(new Map());
				setAudioTracksMap(new Map());
				roomRef.current = null;
				hasSessionsRef.current = false;
			});

			room.connect(result.serverUrl, result.token).catch((err) => {
				console.error('[livekit-room] connect error:', err);
			});
		});

		return () => {
			roomRef.current?.disconnect();
			roomRef.current = null;
			hasSessionsRef.current = false;
		};
	}, [token, meetingId, ingressSessions.length]);

	return (
		<LivekitContext.Provider value={{ tracksMap, audioTracksMap }}>
			{children}
		</LivekitContext.Provider>
	);
}

export function useLivekit(): LivekitContextValue {
	return useContext(LivekitContext);
}